import { FC, useEffect, useRef, useState } from 'react';
import { RenderPosition } from '../../../../enums/RenderPosition';
import { useCheckDevice } from '../../../../hooks/UseCheckDevice';
import { useOutsideClickDetection } from '../../../../hooks/UseOutsideClickDetection';
import { getRenderposition } from '../../../../services/utils/Helpers';
import { MinMaxFilter } from '../../../../types/FilterTypes';
import { DropdownWrapper, Icon } from '../../../style';
import DropdownSelect from '../dropdown-select/DropdownSelect';
import RangeDropdownOption from './RangeDropdownOption';

export type RangeDropdownProps = {
  displayLabel?: string;
  possibleRange: MinMaxFilter<number>;
  selectedValue: MinMaxFilter<number>;
  placeholder?: string;
  bottomLabelText?: string;
  type: 'am-label' | 'am-dropdown';
  maxLength?: number;
  inputType?: string;
  onChange: (value: MinMaxFilter<number>) => void;
  onOpen?: () => void;
  onClose?: () => void;
  cssClass?: string;
  zeroMaxValueAllowed?: boolean;
  selectedLabelPosition?: 'bottom' | 'right';
  optionsLeft?: number;
};

const RangeDropdown: FC<RangeDropdownProps> = ({
  placeholder,
  possibleRange,
  onChange,
  onOpen,
  onClose,
  selectedValue,
  type,
  displayLabel,
  maxLength,
  inputType,
  cssClass = '',
  bottomLabelText = '',
  zeroMaxValueAllowed = false,
  selectedLabelPosition = 'bottom',
  optionsLeft = 0,
}) => {
  const wrapperRef = useRef<HTMLElement>(null);
  const filterHeightRef = useRef<HTMLDivElement>(null);
  const [value, setValue] = useState<MinMaxFilter<number>>(null);
  const [validatedValue, setValidatedValue] = useState<MinMaxFilter<number>>(null);

  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [isChange, setIsChange] = useState<boolean>(false);
  const [filterHeight, setFilterHeight] = useState<number>(0);
  const [renderPosition, setRenderPosition] = useState<RenderPosition>(RenderPosition.Bottom);
  const { isMobileDevice } = useCheckDevice();

  useOutsideClickDetection(wrapperRef, setIsVisible);

  const onHeaderClick = (): void => {
    const bottom = wrapperRef.current?.getBoundingClientRect()?.bottom;
    setRenderPosition(getRenderposition(bottom + 50));
    setTimeout(() => {
      setIsVisible(!isVisible);
    }, 1);
  };

  const handleValueChange = (val: MinMaxFilter<number>) => {
    setValue(val);
    if (
      !val ||
      val.min === null ||
      val.max === null ||
      val.max >= val.min ||
      val.max === undefined ||
      val.min === undefined
    ) {
      setValidatedValue(val);
      setIsChange(true);
    }
  };

  const handleEnterKeyChange = () => {
    if (isChange) {
      onChange(validatedValue);
      setIsVisible(!isVisible);
    } else {
      setIsVisible(!isVisible);
    }
  };

  const handleMouseEvent = (event): void => {
    if (event && event.target) {
      setFilterHeight(event.target.offsetParent?.offsetHeight || 0);
    }
  };

  const label = value
    ? `${value.min === undefined || value.min === null ? 'N/A' : value.min} - ${value.max || (value.max === 0 && zeroMaxValueAllowed) ? value.max : 'N/A'}`
    : null;

  const labelProps =
    selectedLabelPosition === 'bottom' ? { bottomLabel: label } : { inlineLabel: label };

  useEffect(() => {
    setIsChange(false);
    setValue(selectedValue);
    setValidatedValue(selectedValue);
  }, [selectedValue]);

  useEffect(() => {
    setFilterHeight(filterHeightRef.current?.offsetHeight || 0);
  }, [value]);

  useEffect(() => {
    if (!isVisible && isChange) {
      onChange(validatedValue);
      setIsChange(false);
    }
    if (isVisible && onOpen) {
      onOpen();
    }
    if (!isVisible && onClose) {
      onClose();
    } else {
      setValue(selectedValue);
    }
  }, [isVisible]);

  return (
    <DropdownWrapper
      isMobileDevice={isMobileDevice}
      data-testid='range-dropdown'
      flexDirection='row'
      ref={wrapperRef}
      className={type === 'am-label' ? `react-table-header-range ${cssClass}` : `${cssClass}`}
    >
      <div
        className={`heap-filter-item ${type === 'am-dropdown' ? 'w-88' : ''}`}
        ref={filterHeightRef}
      >
        {type === 'am-label' && (
          <>
            <span data-testid='range-dropdown-header'>
              <span className='header-text header-sort'>{displayLabel}</span>
              <div
                className='header-filter-value'
                onClick={onHeaderClick}
                onMouseDown={handleMouseEvent}
              >
                {label + bottomLabelText}
              </div>
            </span>
          </>
        )}
        {type === 'am-dropdown' && (
          <span
            data-testid='range-dropdown-header'
            onClick={onHeaderClick}
            onMouseDown={handleMouseEvent}
          >
            <DropdownSelect name={placeholder} {...labelProps} />
          </span>
        )}
        {isVisible && (
          <RangeDropdownOption
            possibleRange={possibleRange}
            type={type}
            value={value}
            inputType={inputType}
            maxLength={maxLength}
            filterHeight={filterHeight}
            handleValueChange={handleValueChange}
            handleEnterKeyChange={handleEnterKeyChange}
            renderPosition={renderPosition}
            left={isMobileDevice ? 0 : optionsLeft}
          />
        )}
      </div>
      {type === 'am-dropdown' && (
        <div className='vertical-center'>
          <Icon type='dropdown' size={14} />
        </div>
      )}
    </DropdownWrapper>
  );
};

export default RangeDropdown;
